'use client';

import { useEffect, useState, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/store/auth';
import { api } from '@/lib/api';

// Accent color options matching AppearanceTab
const ACCENT_COLORS = ['purple', 'blue', 'green', 'orange', 'pink'];

// Font family options
const FONT_OPTIONS = ['geist-mono', 'jetbrains-mono', 'inter'];

// Font size options
const FONT_SIZE_OPTIONS = ['small', 'medium', 'large'];

// Background color options
const BACKGROUND_COLORS = ['purple', 'blue', 'green', 'teal', 'slate', 'rose'];

interface UserPreferences {
  theme: string;
  accentColor: string;
  fontFamily: string;
  fontSize: string;
  backgroundColor: string;
  language?: 'en' | 'he';
}

// Pages that should always use default appearance (not affected by user settings)
const PUBLIC_PATHS = ['/', '/login', '/register', '/invite'];

/**
 * Component that loads and applies user preferences on mount.
 * Should be rendered within ThemeProvider context.
 */
export function PreferencesLoader() {
  const { isAuthenticated, user } = useAuthStore();
  const { setTheme, theme } = useTheme();
  const pathname = usePathname();
  const [loaded, setLoaded] = useState(false);
  const [currentPrefs, setCurrentPrefs] = useState<UserPreferences | null>(null);

  // Check if current path is a public path that should use defaults
  const isPublicPath = PUBLIC_PATHS.some(p => pathname === p || pathname?.startsWith(`${p}/`));

  // Function to apply preferences to DOM
  // Applies ONLY the custom appearance classes (accent/font/font-size/bg-color).
  // The theme class is owned exclusively by next-themes — do NOT setTheme here.
  const applyPreferences = useCallback((preferences: UserPreferences) => {
    // Apply accent color
    const accentClasses = ACCENT_COLORS.map(c => `accent-${c}`);
    document.documentElement.classList.remove(...accentClasses);
    if (preferences.accentColor && ACCENT_COLORS.includes(preferences.accentColor)) {
      document.documentElement.classList.add(`accent-${preferences.accentColor}`);
    }

    // Apply font family
    const fontClasses = FONT_OPTIONS.map(f => `font-${f}`);
    document.documentElement.classList.remove(...fontClasses);
    if (preferences.fontFamily && FONT_OPTIONS.includes(preferences.fontFamily)) {
      document.documentElement.classList.add(`font-${preferences.fontFamily}`);
      
      // Language-aware variables only (globals.css) — this inline style outranks
      // every stylesheet rule, so a raw --font-geist-mono here would strip Heebo
      // off the Hebrew UI on every authenticated page load.
      document.body.style.fontFamily = preferences.fontFamily === 'inter'
        ? 'var(--font-inter)'
        : preferences.fontFamily === 'jetbrains-mono'
          ? 'var(--font-jetbrains-mono)'
          : 'var(--font-app-mono)';
    }

    // Apply font size
    const fontSizeClasses = FONT_SIZE_OPTIONS.map(s => `font-size-${s}`);
    document.documentElement.classList.remove(...fontSizeClasses);
    if (preferences.fontSize && FONT_SIZE_OPTIONS.includes(preferences.fontSize)) {
      document.documentElement.classList.add(`font-size-${preferences.fontSize}`);
    }

    // Apply background color
    const bgClasses = BACKGROUND_COLORS.map(c => `bg-color-${c}`);
    document.documentElement.classList.remove(...bgClasses);
    if (preferences.backgroundColor && BACKGROUND_COLORS.includes(preferences.backgroundColor)) {
      document.documentElement.classList.add(`bg-color-${preferences.backgroundColor}`);
    }
  }, []);

  // Reset the loader when the effective identity changes (e.g. connect-as).
  useEffect(() => {
    setLoaded(false);
    setCurrentPrefs(null);
  }, [isAuthenticated, user?.id, user?.is_connected_as, user?.original_user_id]);

  // Handle public pages defaults vs user preferences
  useEffect(() => {
    if (isPublicPath) {
      // Remove all custom classes for public pages (landing, login, register, invite)
      document.documentElement.classList.remove(
        ...ACCENT_COLORS.map(c => `accent-${c}`),
        ...FONT_OPTIONS.map(f => `font-${f}`),
        ...FONT_SIZE_OPTIONS.map(s => `font-size-${s}`),
        ...BACKGROUND_COLORS.map(c => `bg-color-${c}`)
      );
      // Theme on public pages is forced to dark via next-themes' forcedTheme
      // prop (see providers.tsx) — do NOT setTheme here.
      document.body.style.fontFamily = '';
    } else if (isAuthenticated && user) {
      // If we have preferences in state, re-apply them
      if (currentPrefs) {
        applyPreferences(currentPrefs);
      } else {
        // Try to apply from localStorage for instant response
        const cached = localStorage.getItem('user-preferences');
        if (cached) {
          try {
            const prefs = JSON.parse(cached);
            setCurrentPrefs(prefs);
            applyPreferences(prefs);
          } catch (e) {}
        }
      }
    }
  }, [pathname, isAuthenticated, user, currentPrefs, applyPreferences, isPublicPath]);

  useEffect(() => {
    // Only load preferences if user is authenticated, we haven't loaded yet, and NOT on public pages
    if (!isAuthenticated || !user || loaded || isPublicPath) return;

    const loadPreferences = async () => {
      try {
        const response = await api.settings.get();
        const preferences = response.data?.preferences as UserPreferences | undefined;

        if (preferences) {
          // Include language (stored at top level of settings, not inside preferences blob)
          const language = response.data?.language === 'he' ? 'he' : 'en';
          // Cache preferences
          localStorage.setItem('user-preferences', JSON.stringify({ ...preferences, language }));
          // Update state and apply (accent/font/font-size/bg-color only)
          setCurrentPrefs({ ...preferences, language });
          applyPreferences({ ...preferences, language });
          // Seed next-themes ONCE from the server theme at login/identity change.
          // Guarded by `loaded` (this effect runs once per identity, and `loaded`
          // is reset on identity change). After this, next-themes is the SOLE
          // owner of the theme class; server/localStorage is write-through only.
          // Handles 'system' correctly because we compare against next-themes'
          // raw `theme` setting (not resolvedTheme).
          if (preferences.theme && preferences.theme !== theme) {
            setTheme(preferences.theme);
          }
        }

        setLoaded(true);
      } catch (error) {
        console.error('Failed to load user preferences:', error);
        setLoaded(true);
      }
    };

    loadPreferences();
  }, [isAuthenticated, user, loaded, pathname, applyPreferences]);

  // This component doesn't render anything
  return null;
}
